import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  clearConstructor,
  setConstructorBaseline,
  type ConstructorValue,
} from '../../features/design-constructor';
import { applyTemplate } from './template';
import { parseUrlTheme } from './parse';
import {
  clearDesignSystem,
  loadDesignSystem,
  saveDesignSystem,
} from './persistence';
import { DEFAULT_TEMPLATE, type Template, type ThemeSource } from './types';
import { ThemeContext, type ThemeContextValue } from './ThemeContext';

interface Props {
  children: ReactNode;
  /** Backend-supplied design_system (v2 ConstructorValue). Wins over URL params and storage. */
  designSystem?: ConstructorValue | undefined;
  /** Backend-supplied template name (separate from design_system). */
  template?: Template | undefined;
}

function isConstructorChild(): boolean {
  return new URLSearchParams(window.location.search).get('design_constructor') === 'child';
}

interface Initial {
  value: ConstructorValue;
  template: Template;
  source: ThemeSource;
}

function emptyValue(): ConstructorValue {
  return { version: 2 };
}

function resolveInitial(
  designSystem?: ConstructorValue,
  template?: Template,
): Initial {
  const url = parseUrlTheme(window.location.href);

  if (designSystem) {
    // backend wins; in child mode URL overlays on top of backend palette
    const value: ConstructorValue =
      isConstructorChild() && url.value
        ? {
            ...designSystem,
            palette: {
              ...(designSystem.palette ?? {}),
              ...(url.value.palette ?? {}),
            },
          }
        : designSystem;
    return {
      value,
      template: template ?? url.template ?? DEFAULT_TEMPLATE,
      source: 'backend',
    };
  }

  const saved = loadDesignSystem();
  if (saved) return { value: saved.value, template: saved.template, source: 'backend' };

  if (url.value || url.template) {
    return {
      value: url.value ?? emptyValue(),
      template: url.template ?? DEFAULT_TEMPLATE,
      source: 'url',
    };
  }

  return { value: emptyValue(), template: DEFAULT_TEMPLATE, source: 'default' };
}

export function ThemeProvider({ children, designSystem, template }: Props) {
  const [state, setState] = useState<Initial>(() => resolveInitial(designSystem, template));

  // Apply to :root on first mount and whenever state changes.
  useEffect(() => {
    applyTemplate(state.template);
    setConstructorBaseline(state.value);
  }, [state.value, state.template]);

  // Re-resolve when the backend props transition from undefined → loaded.
  useEffect(() => {
    if (!designSystem) return;
    const next = resolveInitial(designSystem, template);
    setState(next);
    saveDesignSystem({ template: next.template, value: next.value });
  }, [designSystem, template]);

  const setValue = useCallback((next: ConstructorValue, nextTemplate?: Template) => {
    setState((prev) => {
      const merged: Initial = {
        value: next,
        template: nextTemplate ?? prev.template,
        source: 'backend',
      };
      saveDesignSystem({ template: merged.template, value: merged.value });
      return merged;
    });
  }, []);

  const reset = useCallback(() => {
    clearConstructor();
    clearDesignSystem();
    setState({ value: emptyValue(), template: DEFAULT_TEMPLATE, source: 'default' });
  }, []);

  const ctx = useMemo<ThemeContextValue>(
    () => ({
      value: state.value,
      template: state.template,
      source: state.source,
      setValue,
      reset,
    }),
    [state, setValue, reset],
  );

  return <ThemeContext value={ctx}>{children}</ThemeContext>;
}

